import { useEffect, useMemo, useState } from 'react';

interface UseMyScoreDraftParams {
  tab: string;
  sourceWeights: Array<{ name: string; score: number }>;
  isOpen: boolean;
  onApplySavedScore?: (score: any) => void;
}

export function useMyScoreDraft({ tab, sourceWeights, isOpen, onApplySavedScore }: UseMyScoreDraftParams) {
  const [myScoreThreshold, setMyScoreThreshold] = useState(0.5);
  const [myScoreDraft, setMyScoreDraft] = useState<Array<{ name: string; score: number }>>([]);
  const [isMyScoreSourcePickerOpen, setIsMyScoreSourcePickerOpen] = useState(false);
  const [thresholdAdjustedSources, setThresholdAdjustedSources] = useState(false);
  const [myScoreLimitAdjusted, setMyScoreLimitAdjusted] = useState(false);

  useEffect(() => {
    if (!tab) return;
    if (sourceWeights.length > 0) {
      setMyScoreDraft(sourceWeights.map(sw => ({ name: sw.name, score: Number(sw.score) || 0 })));
      return;
    }
    setMyScoreDraft([]);
  }, [tab, sourceWeights]);

  useEffect(() => {
    setIsMyScoreSourcePickerOpen(false);
  }, [isOpen]);

  const handleMyScoreSliderChange = (sourceName: string, newScore: number) => {
    setMyScoreDraft(prev => {
      const otherTotal = prev
        .filter(source => source.name !== sourceName)
        .reduce((sum, source) => sum + source.score, 0);
      const maxAllowed = Math.max(0, Number((1 - otherTotal).toFixed(2)));
      const adjustedScore = Math.min(Number(newScore.toFixed(2)), maxAllowed);
      setMyScoreLimitAdjusted(adjustedScore < Number(newScore.toFixed(2)));
      return prev.map(source => (source.name === sourceName ? { ...source, score: adjustedScore } : source));
    });
  };

  const handleApplyMyScoreDraft = () => {
    if (!onApplySavedScore) return;
    const normalizedWeights = myScoreDraft.filter(sw => sw.score > 0);
    onApplySavedScore({
      name: 'Inline My Score',
      sourceWeights: normalizedWeights,
    });
  };

  const handleAddMyScoreSource = (sourceName: string) => {
    setMyScoreDraft(prev => {
      if (prev.some(source => source.name === sourceName)) return prev;
      return [...prev, { name: sourceName, score: 0.1 }];
    });
  };

  const handleRemoveMyScoreSource = (sourceName: string) => {
    setMyScoreDraft(prev => prev.filter(source => source.name !== sourceName));
  };

  const handleThresholdChange = (nextThreshold: number) => {
    const roundedThreshold = Number(nextThreshold.toFixed(2));
    setMyScoreThreshold(roundedThreshold);
    if (myScoreDraft.length === 0) {
      setThresholdAdjustedSources(false);
      return;
    }
    const equalValue = Number((roundedThreshold / myScoreDraft.length).toFixed(2));
    setMyScoreDraft(prev => prev.map(source => ({ ...source, score: equalValue })));
    setThresholdAdjustedSources(true);
    setMyScoreLimitAdjusted(false);
  };

  const getSliderTrackStyle = (value: number) => ({
    background: `linear-gradient(to right, #be0080 0%, #be0080 ${value * 100}%, #E6E7F3 ${value * 100}%, #E6E7F3 100%)`,
  });

  const availableSourcesToAdd = useMemo(
    () => (availableScoreSources: string[]) =>
      availableScoreSources.filter(sourceName => !myScoreDraft.some(source => source.name === sourceName)),
    [myScoreDraft],
  );

  return {
    myScoreThreshold,
    myScoreDraft,
    isMyScoreSourcePickerOpen,
    setIsMyScoreSourcePickerOpen,
    thresholdAdjustedSources,
    myScoreLimitAdjusted,
    availableSourcesToAdd,
    handleMyScoreSliderChange,
    handleApplyMyScoreDraft,
    handleAddMyScoreSource,
    handleRemoveMyScoreSource,
    handleThresholdChange,
    getSliderTrackStyle,
  };
}
